/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Container, Row, Col } from "react-awesome-styled-grid";
import { useParams } from "react-router-dom";
import { Box, Spacer, Text } from "@nordcloud/gnui";
import { Breadcrumbs, showSuccess } from "~/components";
import { useShowError } from "~/components/Error/useShowError";
import { InlineEditSwitch } from "~/components/InlineEdit/InlineEditSwitch";
import { InlineEditText } from "~/components/InlineEdit/InlineEditText";
import { DetailRow } from "~/components/Utils/DetailRow";
import { navItems } from "~/constants";
import { Tool } from "~/models";
import { ACTIONS, ROUTES, useAccess } from "~/routing";
import {
  generateActionSuccessText,
  useAxios,
  useDetailsNotFound,
} from "~/tools";
import { ToolValues } from "./types";

type RouteParams = {
  toolId: string;
};

function ToolDetails() {
  const state = useParams<RouteParams>();
  const { toolId } = state;
  const [tool, setTool] = React.useState<Tool>({
    id: "",
    name: "",
    description: "",
    internal: false,
    deprecated: false,
  });

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const { detailsNotFound } = useDetailsNotFound();

  const { hasAccess: canEdit } = useAccess(ACTIONS.EDIT_TOOL);

  const fetchTool = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/tools/${toolId}`);
      setTool({
        id: data?.id,
        name: data?.name,
        description: data?.description,
        internal: data?.internal,
        deprecated: data?.deprecated,
      });
    } catch (e) {
      detailsNotFound(ROUTES.tool.index, navItems.tool);
    }
  }, [Axios, detailsNotFound, toolId]);

  React.useEffect(() => {
    fetchTool();
  }, [fetchTool]);

  const onSubmit = async (values: Tool | ToolValues) => {
    const organizeValues = {
      ...tool,
      name: values.name,
      description: values.description,
      internal: values.internal,
    };
    try {
      await Axios().put(`/tools/${toolId}`, organizeValues);
      showSuccess(generateActionSuccessText(`Tool`)()("updated")());
      fetchTool();
    } catch (e) {
      showError(e.response);
    }
  };

  const breadcrumbLabels = tool
    ? [{ value: tool?.id, label: tool?.name }]
    : undefined;

  return (
    <Container>
      <Breadcrumbs title={tool?.name || ""} labels={breadcrumbLabels} />
      <Box spacing="spacing04">
        <Row>
          <Col xs={8} sm={8} md={12} lg={12}>
            <Text weight="medium">Tool Details</Text>

            <DetailRow title="Name" minWidth="7rem">
              <InlineEditText
                name="name"
                defaultValue={tool.name}
                width="400px"
                canEdit={canEdit}
                onSubmit={(name) => onSubmit({ ...tool, name })}
              />
            </DetailRow>

            <DetailRow title="Internal" minWidth="7rem">
              <InlineEditSwitch
                defaultValue={tool.internal}
                canEdit={canEdit}
                onSubmit={(internal) => onSubmit({ ...tool, internal })}
              />
            </DetailRow>

            <DetailRow title="Description" minWidth="7rem">
              <InlineEditText
                textArea
                name="description"
                defaultValue={tool.description}
                canEdit={canEdit}
                onSubmit={(description) => onSubmit({ ...tool, description })}
              />
            </DetailRow>

            {tool?.deprecated && (
              <>
                <Spacer height=".5rem" />
                <Text color="warning">This tool has been removed</Text>
              </>
            )}
          </Col>
        </Row>
      </Box>
    </Container>
  );
}

export { ToolDetails };
